import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { GraduationCap, Users, Headphones, Wrench, BellRing, Shield, ArrowRight, ArrowUpRight, Layers, Briefcase } from "lucide-react";

const WORKFORCE_IMG = "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1600&q=80";
const SOC_IMG = "https://images.unsplash.com/photo-1581090700227-1e37b190418e?auto=format&fit=crop&w=1400&q=80";

const SECTIONS = [
  { icon: Briefcase, title: "Workforce Deployment", desc: "Embedded cybersecurity professionals — placed on-site or remote — across enterprise programs and project deployments.",
    bullets: ["On-site SOC and security analysts", "Deployment engineers for endpoint, DLP and ZTNA rollouts", "Awareness program facilitators", "Project-based technical staffing"] },
  { icon: Headphones, title: "Enterprise Technical Support", desc: "Dedicated technical resources for managed customers — frontline triage, escalation handling and operational continuity.",
    bullets: ["L1 / L2 support engineers", "Tooling and SIEM administrators", "Vendor coordination", "Service-level reporting"] },
  { icon: Wrench, title: "Contractual Technical Resources", desc: "Flexible long- and short-term contracts for cybersecurity manpower — billed by program, headcount or milestone.",
    bullets: ["6 / 12 / 24-month contracts", "Project-based deployment", "Hybrid + on-site staffing", "Backfill and surge capacity"] },
  { icon: Shield, title: "Security Operations Support", desc: "Cosmos Defence SOC capacity available as augmentation for your existing security team.",
    bullets: ["Tier-1 and Tier-2 SOC analysts", "Threat hunting specialists", "Incident response coordinators", "Reporting and metrics analysts"] },
  { icon: GraduationCap, title: "Internship & Talent Pipeline", desc: "Structured cybersecurity internships feeding directly into enterprise deployment teams.",
    bullets: ["3, 6 and 12-month tracks", "Hands-on SOC, awareness and engineering rotations", "Mentorship and certifications", "Conversion to full-time roles"] },
  { icon: BellRing, title: "Awareness Program Teams", desc: "On-the-ground awareness coordinators running cyber-safety drives across India.",
    bullets: ["Workshop facilitators", "Multilingual trainers (Hindi + regional)", "Senior-citizen specialists", "Education-sector coordinators"] },
];

const PIPELINE = [
  { label: "Skill India Aligned Curriculum", desc: "Cyber roles mapped to national skilling frameworks." },
  { label: "Hands-on Labs & SOC Simulations", desc: "Real telemetry, real playbooks — not slideware." },
  { label: "Industry-Ready Soft Skills", desc: "Reporting, customer interaction and on-the-job rotation." },
  { label: "Continuous Career Development", desc: "Quarterly skill reviews and certification support." },
];

export default function Workforce() {
  return (
    <div data-testid="workforce-page">
      <PageHero
        eyebrow="Division 03 · Cyber Workforce"
        title="Cybersecurity workforce & technical resource ecosystem"
        description="A workforce ecosystem — not a job board. Cosmos Defence builds, trains, deploys and sustains the cybersecurity professionals that run modern security operations across enterprises, institutions and large-scale projects."
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact?type=consultation&service=workforce" className="btn-accent" data-testid="workforce-build-cta">
            Build Your Cybersecurity Workforce <ArrowUpRight size={16} />
          </Link>
          <Link to="/contact?type=support&service=resources" className="btn-primary" data-testid="workforce-request-cta">
            Request Technical Resources
          </Link>
        </div>
      </PageHero>

      {/* Positioning visual */}
      <section className="py-16 bg-white">
        <div className="container-cd grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-amber-200/40 to-blue-200/40 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-[1.75rem] overflow-hidden border border-slate-200/60">
              <img src={WORKFORCE_IMG} alt="Cybersecurity workforce" className="w-full h-[440px] object-cover" />
              <div className="absolute top-5 left-5 glass-card rounded-2xl px-4 py-3">
                <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">Workforce Ecosystem</div>
                <div className="font-display font-bold text-sm text-[#0A1B5E]">Train · Deploy · Sustain</div>
              </div>
            </div>
          </div>
          <div>
            <span className="badge-cd">Why Workforce</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
              Tools alone don't secure enterprises — people do.
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Even the best stack fails without skilled hands operating it. We solve the cybersecurity talent gap with a vertically-integrated workforce ecosystem — covering training, internships, deployment engineering and long-term technical resource support.
            </p>
            <ul className="mt-6 space-y-3 text-slate-700">
              {["Project-based technical staffing", "Long-term contractual resources", "SOC analyst augmentation", "Awareness program coordinators"].map((p) => (
                <li key={p} className="flex items-center gap-2"><Layers size={14} className="text-[#3B82F6]" /> {p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 6 service blocks */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Workforce Services"
            title="Six ways we deploy cyber talent into your organization"
            description="From short-burst project staffing to multi-year embedded teams, our workforce model adapts to your operating tempo."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTIONS.map((s, i) => (
              <div key={s.title} data-testid={`workforce-service-${i}`} className="card-cd">
                <span className="h-12 w-12 grid place-items-center rounded-xl bg-gradient-to-br from-amber-500/10 to-rose-500/10 border border-amber-100 text-amber-600">
                  <s.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-[#0A1B5E]">{s.title}</h3>
                <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{s.desc}</p>
                <ul className="mt-5 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                      <Layers size={12} className="text-[#3B82F6] mt-1 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Talent pipeline */}
      <section className="py-20 bg-white">
        <div className="container-cd grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="badge-cd">Talent Pipeline</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#0A1B5E] leading-tight">
              A pipeline — not a placement agency
            </h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">
              Every cyber professional we deploy is shaped by a structured training, internship and mentorship pathway — so what you receive is operationally ready, not just certified.
            </p>
            <ul className="mt-8 space-y-5">
              {PIPELINE.map((p, i) => (
                <li key={p.label} data-testid={`workforce-pipeline-${i}`} className="flex items-start gap-4">
                  <span className="h-10 w-10 grid place-items-center rounded-xl gradient-cta text-white shrink-0">
                    <Users size={18} />
                  </span>
                  <div>
                    <div className="font-display font-bold text-[#0A1B5E]">{p.label}</div>
                    <div className="text-sm text-slate-600 mt-0.5">{p.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
            <Link to="/careers" className="btn-ghost mt-10" data-testid="workforce-careers-link">
              Join the talent pipeline <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-blue-200/40 to-cyan-200/40 rounded-[2rem] blur-2xl" />
            <div className="relative rounded-[1.75rem] overflow-hidden border border-slate-200/60">
              <img src={SOC_IMG} alt="SOC operations" className="w-full h-[440px] object-cover" />
              <div className="absolute bottom-5 right-5 glass-card rounded-2xl px-4 py-3">
                <div className="font-mono-tech text-[10px] tracking-widest text-slate-500 uppercase">Deployable Roles</div>
                <div className="font-display font-bold text-sm text-[#0A1B5E]">SOC · Engineer · Coordinator · Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final dual CTA */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container-cd grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-cd !p-10 ring-glow-cyan border-cyan-200">
            <h3 className="font-display text-2xl font-bold text-[#0A1B5E]">Build Your Cybersecurity Workforce</h3>
            <p className="mt-3 text-slate-600">Long-term workforce architecture for enterprises, institutions and large projects.</p>
            <Link to="/contact?type=consultation&service=workforce" className="btn-accent mt-6" data-testid="workforce-final-build">
              Start a Workforce Plan <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="card-cd !p-10 ring-glow-purple border-purple-200">
            <h3 className="font-display text-2xl font-bold text-[#0A1B5E]">Request Technical Resources</h3>
            <p className="mt-3 text-slate-600">On-demand staffing for SOC, deployment, support and program coordination.</p>
            <Link to="/contact?type=support&service=resources" className="btn-primary mt-6" data-testid="workforce-final-request">
              Request Resources <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
